import React, { FC, useEffect } from 'react';
import { Redirect } from 'react-router-dom';
import * as Sentry from '@sentry/browser';
import { isNumber } from 'lodash';
import { useTranslation } from 'react-i18next';

import { convertSessionScheduledTimeToStartAndEndTime } from 'src/modules/videoSessions/helpers/sessionSlotToReadableDate';
import { useValidatePendingCoachingVisitMutation } from 'src/modules/insuranceIntegration/documents/ValidatePendingCoachingVisit.generated';
import { SessionReview } from 'src/modules/payments/components/SessionReview';
import { CoachingVisitState } from 'src/generated/webapp_gql';
import type { CoachingVisit } from 'src/modules/videoSessions/rematchModels/videoSessions';
import { toastNotificationsService } from 'src/config/rootService';
import { config } from 'src/config/config';
import { captureException } from 'src/shared/helpers/captureException';

type Props = {
    session: CoachingVisit;
    isSuccess?: boolean;
};

export const DeeplinkedSessionReview: FC<Props> = ({ session, isSuccess }) => {
    const { t } = useTranslation();

    const canSkipPendingCoachingVisitValidation =
        session.state === CoachingVisitState.Scheduled && isSuccess;

    const [
        validatePendingCoachingVisitMutation,
        {
            data: validatePendingCoachingVisitData,
            error: validatePendingCoachingVisitError,
            loading: isValidatingPendingCoachingVisit,
        },
    ] = useValidatePendingCoachingVisitMutation({});

    const costEstimation =
        validatePendingCoachingVisitData?.validatePendingCoachingVisit?.costEstimation ?? undefined;
    const patientResponsibility = costEstimation?.patientResponsibility ?? undefined;

    useEffect(() => {
        if (
            canSkipPendingCoachingVisitValidation ||
            isValidatingPendingCoachingVisit ||
            isNumber(patientResponsibility) ||
            validatePendingCoachingVisitData ||
            validatePendingCoachingVisitError
        )
            return;

        validatePendingCoachingVisitMutation({
            variables: { coachingVisitUuid: session.uuid },
        });
    }, [
        canSkipPendingCoachingVisitValidation,
        isValidatingPendingCoachingVisit,
        patientResponsibility,
        session,
        validatePendingCoachingVisitMutation,
        validatePendingCoachingVisitData,
        validatePendingCoachingVisitError,
    ]);

    const { scheduledEndTime: end, scheduledStartTime: start } =
        convertSessionScheduledTimeToStartAndEndTime(session);

    if (!(end && start)) {
        return <Redirect to="/dashboard" />;
    }

    if (
        !canSkipPendingCoachingVisitValidation &&
        (validatePendingCoachingVisitData || validatePendingCoachingVisitError) &&
        !isValidatingPendingCoachingVisit &&
        !isNumber(patientResponsibility)
    ) {
        toastNotificationsService.renderErrorToast(t(config.messages.genericError));

        Sentry.withScope(scope => {
            scope.setExtra('response', validatePendingCoachingVisitData);

            const errMsg =
                'Did not receive correct payload in the response in useValidatePendingCoachingVisitMutation: session has no patientResponsibility';
            Sentry.captureException(new Error(errMsg));
        });

        captureException(
            new Error(
                'Did not receive correct payload in the response in useValidatePendingCoachingVisitMutation: session has no patientResponsibility',
            ),
        );
        return <Redirect to="/dashboard" />;
    }

    const provider = session?.coachAssignment?.coach;

    if (!provider) {
        Sentry.withScope(scope => {
            scope.setExtra('coaching_visit_uuid', session.uuid);

            Sentry.captureException(
                new Error('[SessionReviewPage] Could not find provider object'),
            );
        });

        captureException(new Error('[SessionReviewPage] Could not find provider object'), {
            coaching_visit_uuid: session.uuid,
        });
        return <Redirect to="/dashboard" />;
    }

    return (
        <SessionReview
            chargeAmount={patientResponsibility}
            costEstimation={costEstimation}
            dataTestid="DeeplinkedSessionReview.TestID"
            state={session.state}
            isLoadingPaymentInfo={isValidatingPendingCoachingVisit}
            provider={provider}
            sessionUuid={session.uuid}
            start={start}
            end={end}
            isSuccess={isSuccess}
        />
    );
};
